import React, { useState } from 'react';
import { FiLock, FiX, FiCheck, FiLoader } from 'react-icons/fi';
import { getApiEndpoint } from '../utils/api';
import { getAuthHeaders } from '../utils/auth';
import './ClaimAccountBanner.css';

/**
 * Banner + modal that prompts magic-link users to set a password so they
 * can access their account from any device.
 *
 * Shows when user.has_password === false.
 * Dismissible per session (not permanently) since it's important.
 */
export default function ClaimAccountBanner({ user, onPasswordSet }) {
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!user || user.has_password || dismissed) return null;

  const handleSubmit = async () => {
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/auth/set-password'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to set password');
      }
      setSuccess(true);
      if (onPasswordSet) onPasswordSet();
      setTimeout(() => { setShowModal(false); }, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="claim-banner">
        <FiLock className="claim-banner-icon" />
        <span>
          <strong>Save your work</strong> — set a password so you can log in from any device.
        </span>
        <div className="claim-banner-actions">
          <button className="claim-banner-cta" onClick={() => setShowModal(true)}>
            Set password
          </button>
          <button className="claim-banner-dismiss" onClick={() => setDismissed(true)} title="Hide for this session">
            <FiX />
          </button>
        </div>
      </div>

      {showModal && (
        <div className="claim-modal-overlay" onClick={() => !loading && setShowModal(false)}>
          <div className="claim-modal" onClick={e => e.stopPropagation()}>
            <h3>Set your password</h3>
            <p className="claim-modal-hint">
              Your account: <strong>{user.email}</strong>
              <br />
              Once you set a password, your magic link will stop working — log in normally from then on.
            </p>

            {success ? (
              <div className="claim-success">
                <FiCheck /> Password set! You can now log in from any device.
              </div>
            ) : (
              <>
                <div className="claim-field">
                  <label>New password (8+ characters)</label>
                  <input
                    type="password"
                    autoFocus
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                </div>
                <div className="claim-field">
                  <label>Confirm password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                </div>

                {error && <div className="claim-error">{error}</div>}

                <div className="claim-modal-actions">
                  <button className="claim-btn-outline" onClick={() => setShowModal(false)} disabled={loading}>
                    Cancel
                  </button>
                  <button className="claim-btn-primary" onClick={handleSubmit} disabled={loading || !password || !confirm}>
                    {loading ? <><FiLoader className="claim-spin" /> Setting...</> : 'Set password'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
